<div class="dashboard-container">
  <!-- Welcome Header -->
  <div class="welcome-section">
    <div class="welcome-content">
      <h1 class="welcome-title">
        Welcome back, <span class="highlight">{{ authService.getCurrentUser()?.firstName }}!</span>
      </h1>
      <p class="welcome-subtitle">Manage your family connections and explore your family tree</p>
    </div>
    <div class="user-avatar">
      <div class="avatar-circle">
        {{ (authService.getCurrentUser()?.firstName?.charAt(0) || 'U') + (authService.getCurrentUser()?.lastName?.charAt(0) || '') }}
      </div>
    </div>
  </div>

  <!-- Stats Overview -->
  <div class="stats-grid">
    <div class="stat-card" *ngIf="!isLoading && !error">
      <div class="stat-icon family-icon">👨‍👩‍👧‍👦</div>
      <div class="stat-content">
        <h3 class="stat-number">{{ animatedStats.familyMembers }}</h3>
        <p class="stat-label">Total Relatives</p>
      </div>
    </div>
    <div class="stat-card" *ngIf="!isLoading && !error">
      <div class="stat-icon connection-icon">🔗</div>
      <div class="stat-content">
        <h3 class="stat-number">{{ animatedStats.connections }}</h3>
        <p class="stat-label">Active Relatives</p>
      </div>
    </div>
    <div class="stat-card" *ngIf="!isLoading && !error">
      <div class="stat-icon tree-icon">🌳</div>
      <div class="stat-content">
        <h3 class="stat-number">{{ animatedStats.generations }}</h3>
        <p class="stat-label">Generations</p>
      </div>
    </div>
    <div class="stat-card" *ngIf="!isLoading && !error">
      <div class="stat-icon activity-icon">📊</div>
      <div class="stat-content">
        <h3 class="stat-number">{{ animatedStats.recentUpdates }}</h3>
        <p class="stat-label">Recent Signups</p>
      </div>
    </div>
    
    <!-- Loading State -->
    <div class="stat-card loading-card" *ngIf="isLoading">
      <div class="stat-icon loading-icon">⏳</div>
      <div class="stat-content">
        <h3 class="stat-number">...</h3>
        <p class="stat-label">Loading Dashboard Data</p>
      </div>
    </div>
    
    <!-- Error State -->
    <div class="stat-card error-card" *ngIf="error">
      <div class="stat-icon error-icon">⚠️</div>
      <div class="stat-content">
        <h3 class="stat-number">!</h3>
        <p class="stat-label">{{ error }}</p>
      </div>
    </div>
  </div>

  <!-- Main Content Grid -->
  <div class="content-grid">
    <!-- Quick Actions -->
    <div class="content-section">
      <h2 class="section-title">Quick Actions</h2>
      <div class="action-cards">
        <div class="action-card primary-action">
          <div class="action-icon">🌳</div>
          <div class="action-content">
            <h4>View Family Tree</h4>
            <p>Explore your family connections and relationships</p>
            <a [routerLink]="['/hierarchy', getCurrentUserId()]" class="btn btn-primary action-btn">
              <span>View Hierarchy</span>
              <i class="arrow">→</i>
            </a>
          </div>
        </div>
        <div class="action-card success-action">
          <div class="action-icon">👥</div>
          <div class="action-content">
            <h4>Add Family Member</h4>
            <p>Invite new family members to join your tree</p>
            <a routerLink="/manage-relatives" [queryParams]="{action: 'add'}" class="btn btn-success action-btn">
              <span>Add Relative</span>
              <i class="arrow">→</i>
            </a>
          </div>
        </div>
        <div class="action-card invite-action">
          <div class="action-icon">📨</div>
          <div class="action-content">
            <h4>Invite a Relative</h4>
            <p>Send SMS invitation to family members</p>
            <a routerLink="/invite-relative" class="btn btn-invite action-btn">
              <span>Send Invite</span>
              <i class="arrow">→</i>
            </a>
          </div>
        </div>
        <div class="action-card info-action">
          <div class="action-icon">📝</div>
          <div class="action-content">
            <h4>Manage Relatives</h4>
            <p>Edit and organize your family information</p>
            <a routerLink="/manage-relatives" class="btn btn-info action-btn">
              <span>Manage Relatives</span>
              <i class="arrow">→</i>
            </a>
          </div>
        </div>
        <div class="action-card warning-action" *ngIf="authService.isAdmin()">
          <div class="action-icon">✅</div>
          <div class="action-content">
            <h4>Approve Relatives</h4>
            <p>Review and approve pending family member requests</p>
            <a routerLink="/approve-relatives" class="btn btn-warning action-btn">
              <span>Approve Relatives</span>
              <i class="arrow">→</i>
            </a>
          </div>
        </div>
        <div class="action-card relationship-action">
          <div class="action-icon">🔗</div>
          <div class="action-content">
            <h4>Relationship Calculator</h4>
            <p>Discover the exact relationship between any two family members</p>
            <a routerLink="/relationship-calculator" class="btn btn-relationship action-btn">
              <span>Calculate Relationship</span>
              <i class="arrow">→</i>
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Account Information -->
    <div class="content-section">
      <h2 class="section-title">Account Information</h2>
      <div class="account-card">
        <div class="account-info">
          <div class="info-item">
            <div class="info-label">Username</div>
            <div class="info-value">{{ authService.getCurrentUser()?.username }}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Email</div>
            <div class="info-value">{{ authService.getCurrentUser()?.email }}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Account Type</div>
            <div class="info-value">
              <span class="account-badge" [class.admin]="authService.isAdmin()">
                {{ authService.isAdmin() ? 'Administrator' : 'Family Member' }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Recent Activity -->
    <div class="content-section">
      <h2 class="section-title">Recent Activity</h2>
      <div class="activity-card">
        <div class="activity-empty">
          <div class="empty-icon">📋</div>
          <h4>No Recent Activity</h4>
          <p>Your family activity will appear here as you and your relatives make updates.</p>
        </div>
      </div>
    </div>
  </div>
</div>
